/**
 * PC 站点 ↔ 移动端 H5（/h5）设备适配跳转
 * - 仅手机访问 PC 页 → 追加 /h5 前缀跳转至 uni-app H5
 * - PC / 平板访问 /h5 业务页 → 去掉 /h5 跳转至 PC
 */

import { isPhoneBrowser, shouldUsePcWebsite } from './deviceDetect'
import { isDeviceRedirectEnabled } from './deviceRedirectGray'

export { isDeviceRedirectEnabled, DEVICE_REDIRECT_MASTER_ENABLED, DEVICE_REDIRECT_GRAY_MODE, DEVICE_REDIRECT_GRAY_PERCENT, DEVICE_REDIRECT_GRAY_USER_IDS } from './deviceRedirectGray'

export const H5_SITE_PREFIX = '/h5'
export const H5_DEFAULT_PATH = '/pages/index/index'
/** 登录后业务首页（工作台）；普通 H5↔PC 互跳落点 */
export const PC_DEFAULT_PATH = '/dashboard'
/**
 * 宣传/邀请落地页（仅 PC/平板）
 * - PC 新生成的邀请好友外链直接指向此路径
 * - 旧链 `/h5?regcode=` 在 PC/平板打开时，由 mapH5 按 regcode 特判落到此路径
 * - 旧链 `/?regcode=`、`/dashboard?regcode=` 由 router 守卫落到此路径（仅 PC/平板）
 * - 勿把普通 H5 首页互跳改成此路径
 */
export const PC_LANDING_PATH = '/home'

const WRITE_PAPER_PC_SUFFIX_TO_TEMPLATE = {
  alphabet: 'alphabet',
  wordTrace: 'wordTrace',
  wordHalfTrace: 'wordHalfTrace',
  articleUpperHalf: 'articleUpperHalf',
  word: 'word',
  article: 'article',
}

const WRITE_PAPER_TEMPLATE_TO_PC_SUFFIX = Object.fromEntries(
  Object.entries(WRITE_PAPER_PC_SUFFIX_TO_TEMPLATE).map(([suffix, template]) => [template, suffix]),
)

/** PC 路由（不含 query/hash）→ H5 路由 */
const PC_TO_H5_EXACT = {
  '/': H5_DEFAULT_PATH,
  '/dashboard': H5_DEFAULT_PATH,
  /** 邀请分享 /home?regcode= 被手机打开 → H5 首页保留 query */
  '/home': H5_DEFAULT_PATH,
  '/login': '/packageAuth/login/login',
  '/englishLearning/batchEditing': '/packageEnglishLearning/course-plaza/course-plaza',
  '/englishLearning/courseDetail': '/packageEnglishLearning/course-detail/course-detail',
  '/englishLearning/distribution': '/packageMine/distribution/index',
  '/englishLearning/learningRecord': '/pages/mine/mine',
  '/englishLearning/myCourse': '/pages/index/index',
  '/englishLearning/wordLookup': '/packageEnglishLearning/word-lookup/word-lookup',
  '/englishLearning/writePaper': '/packageEnglishLearning/write-paper/write-paper',
  '/englishLearning/phonics': '/packageEnglishLearning/phonics/phonics',
  '/englishLearning/rankingList': '/pages/rank/rank',
  '/englishLearning/vip': '/packageMine/member-center/member-center',
  '/englishLearning/aICreationWorkshop': '/pages/aicourse/aicourse',
  '/englishLearning/honorPartner': '/packageMine/honor-partner/honor-partner',
}

/** H5 路由 → PC 路由 */
const H5_TO_PC_EXACT = {
  /** 无 regcode：工作台；带 regcode 的邀请入口见 mapH5PathToPc 特判 → /home */
  '/pages/index/index': PC_DEFAULT_PATH,
  '/pages/aicourse/aicourse': '/englishLearning/aICreationWorkshop',
  '/pages/wrongbook/wrongbook': '/englishLearning/batchEditing',
  '/pages/rank/rank': '/englishLearning/rankingList',
  '/pages/mine/mine': PC_DEFAULT_PATH,
  '/packageAuth/login/login': '/login',
  '/packageAuth/forgot-password/forgot-password': '/login',
  '/packageAuth/bind-mobile/bind-mobile': '/login',
  '/packageAuth/set-login-password/set-login-password': '/login',
  '/packageAuth/protocol/protocol': PC_DEFAULT_PATH,
  '/packageEnglishLearning/course-plaza/course-plaza': '/englishLearning/batchEditing',
  '/packageEnglishLearning/course-search/course-search': '/englishLearning/batchEditing',
  '/packageEnglishLearning/course-detail/course-detail': '/englishLearning/courseDetail',
  '/packageEnglishLearning/word-lookup/word-lookup': '/englishLearning/wordLookup',
  '/packageEnglishLearning/write-paper/write-paper': '/englishLearning/writePaper',
  '/packageEnglishLearning/phonics/phonics': '/englishLearning/phonics',
  '/packageMine/distribution/index': '/englishLearning/distribution',
  '/packageMine/honor-partner/honor-partner': '/englishLearning/honorPartner',
  '/packageMine/member-center/member-center': '/englishLearning/vip',
  '/packageMine/settings/settings': PC_DEFAULT_PATH,
  '/packageMine/feedback/feedback': PC_DEFAULT_PATH,
  '/packageMine/edit-profile/edit-profile': PC_DEFAULT_PATH,
  '/packageMine/order-records/order-records': '/englishLearning/vip',
  '/packageMine/announce-list/announce-list': PC_DEFAULT_PATH,
  '/packageAicourse/course-edit/course-edit': '/englishLearning/aICreationWorkshop',
  '/packageAicourse/chapter-manage/chapter-manage': '/englishLearning/aICreationWorkshop',
  '/packageAicourse/chapter-edit/chapter-edit': '/englishLearning/aICreationWorkshop',
  '/packageAicourse/chapter-ai/chapter-ai': '/englishLearning/aICreationWorkshop',
  '/packageAicourse/question-manage/question-manage': '/englishLearning/aICreationWorkshop',
}

function normalizePathname(pathname) {
  let path = String(pathname || '/').trim()
  if (!path.startsWith('/')) path = `/${path}`
  path = path.replace(/\/+$/, '') || '/'
  return path
}

function splitPathQueryHash(input) {
  const raw = String(input || '')
  const hashIdx = raw.indexOf('#')
  const hash = hashIdx >= 0 ? raw.slice(hashIdx) : ''
  const withoutHash = hashIdx >= 0 ? raw.slice(0, hashIdx) : raw
  const qIdx = withoutHash.indexOf('?')
  const pathname = normalizePathname(qIdx >= 0 ? withoutHash.slice(0, qIdx) : withoutHash)
  const search = qIdx >= 0 ? withoutHash.slice(qIdx) : ''
  return { pathname, search, hash }
}

function mergePathQueryHash(pathname, search = '', hash = '') {
  return `${pathname}${search || ''}${hash || ''}`
}

function isLocalDevHost(hostname) {
  const host = String(hostname || '').toLowerCase()
  return host === 'localhost' || host === '127.0.0.1' || host === '[::1]'
}

function hasSkipRedirectQuery(search) {
  try {
    const params = new URLSearchParams(String(search || '').replace(/^\?/, ''))
    return params.get('stay') === '1' || params.get('no_device_redirect') === '1'
  } catch (e) {
    return false
  }
}

/** 邀请好友外链特征：首页入口且带 regcode（旧 /h5?regcode=、新链经互跳后的 H5 首页） */
function hasInviteRegcodeQuery(search) {
  try {
    const params = new URLSearchParams(String(search || '').replace(/^\?/, ''))
    const code = params.get('regcode')
    return !!(code && String(code).trim())
  } catch (e) {
    return false
  }
}

/** 静态资源 / 独立落地页，不参与设备跳转 */
export function shouldSkipDeviceRedirect(pathname, search = '') {
  const path = normalizePathname(pathname)
  if (hasSkipRedirectQuery(search)) return true
  if (path.startsWith(`${H5_SITE_PREFIX}/assets/`)) return true
  if (/\.(js|css|png|jpe?g|gif|ico|svg|woff2?|json|map|txt|xml|webp|mp3|mp4|wasm)(\?|$)/i.test(path)) {
    return true
  }
  const staticHtml = new Set([
    `${H5_SITE_PREFIX}/register.html`,
    `${H5_SITE_PREFIX}/privacy.html`,
    `${H5_SITE_PREFIX}/haol.html`,
    '/register.html',
    '/privacy.html',
    '/haol.html',
    '/cbtutorial.html',
  ])
  return staticHtml.has(path)
}

function mapPcPathToH5(pathname, search) {
  const path = normalizePathname(pathname)
  if (path.startsWith(`${H5_SITE_PREFIX}/`) || path === H5_SITE_PREFIX) return ''

  const exact = PC_TO_H5_EXACT[path]
  if (exact) return mergePathQueryHash(exact, search)

  const phonicsMatch = path.match(/^\/englishLearning\/phonics\/([^/]+)$/)
  if (phonicsMatch) {
    const params = new URLSearchParams(String(search || '').replace(/^\?/, ''))
    params.set('chapterId', decodeURIComponent(phonicsMatch[1]))
    const qs = params.toString()
    return mergePathQueryHash('/packageEnglishLearning/phonics/lesson', qs ? `?${qs}` : '')
  }

  const writePaperMatch = path.match(/^\/englishLearning\/writePaper\/([^/]+)$/)
  if (writePaperMatch) {
    const suffix = writePaperMatch[1]
    const template = WRITE_PAPER_PC_SUFFIX_TO_TEMPLATE[suffix]
    if (template) {
      const params = new URLSearchParams(String(search || '').replace(/^\?/, ''))
      params.set('template', template)
      const qs = params.toString()
      return mergePathQueryHash('/packageEnglishLearning/write-paper/generate', qs ? `?${qs}` : '')
    }
  }

  if (path.startsWith('/englishLearning/')) {
    return mergePathQueryHash(H5_DEFAULT_PATH, search)
  }

  return mergePathQueryHash(H5_DEFAULT_PATH, search)
}

function mapH5PathToPc(pathname, search) {
  let path = normalizePathname(pathname)
  if (path === H5_SITE_PREFIX) path = H5_DEFAULT_PATH
  if (path.startsWith(`${H5_SITE_PREFIX}/`)) {
    path = normalizePathname(path.slice(H5_SITE_PREFIX.length))
  }

  // 仅「邀请好友」：H5 首页 + regcode → 宣传页；无 regcode 走普通互跳进工作台
  if (path === H5_DEFAULT_PATH && hasInviteRegcodeQuery(search)) {
    return mergePathQueryHash(PC_LANDING_PATH, search)
  }

  const exact = H5_TO_PC_EXACT[path]
  if (exact) return mergePathQueryHash(exact, search)

  if (path === '/packageEnglishLearning/phonics/lesson') {
    const params = new URLSearchParams(String(search || '').replace(/^\?/, ''))
    const chapterId = params.get('chapterId')
    if (chapterId) {
      params.delete('chapterId')
      const qs = params.toString()
      return mergePathQueryHash(`/englishLearning/phonics/${encodeURIComponent(chapterId)}`, qs ? `?${qs}` : '')
    }
    return mergePathQueryHash('/englishLearning/phonics', search)
  }

  if (path === '/packageEnglishLearning/write-paper/generate') {
    const params = new URLSearchParams(String(search || '').replace(/^\?/, ''))
    const template = params.get('template')
    const suffix = template ? WRITE_PAPER_TEMPLATE_TO_PC_SUFFIX[template] : ''
    params.delete('template')
    const qs = params.toString()
    if (suffix) {
      return mergePathQueryHash(`/englishLearning/writePaper/${suffix}`, qs ? `?${qs}` : '')
    }
    return mergePathQueryHash('/englishLearning/writePaper', search)
  }

  if (path === '/packageGame/play/play') {
    const params = new URLSearchParams(String(search || '').replace(/^\?/, ''))
    if (params.get('from') === 'record') {
      return mergePathQueryHash('/englishLearning/learningRecord', search)
    }
    const courseId = params.get('courseId')
    if (courseId) {
      const next = new URLSearchParams()
      next.set('id', courseId)
      ;['chapter_type', 'chapterType', 'studyType', 'regcode'].forEach((key) => {
        const val = params.get(key)
        if (val != null && val !== '') next.set(key, val)
      })
      const qs = next.toString()
      return mergePathQueryHash('/englishLearning/courseDetail', qs ? `?${qs}` : '')
    }
    return mergePathQueryHash('/englishLearning/batchEditing', search)
  }

  if (path.startsWith('/packageMine/') || path.startsWith('/packageAuth/') || path.startsWith('/packageAicourse/')) {
    return mergePathQueryHash(PC_DEFAULT_PATH, search)
  }

  if (path.startsWith('/pages/') || path.startsWith('/package')) {
    return mergePathQueryHash(PC_DEFAULT_PATH, search)
  }

  return mergePathQueryHash(PC_DEFAULT_PATH, search)
}

function appendPcVipHash(pathname, hash, search) {
  if (hash) return hash
  if (pathname !== '/englishLearning/vip') return ''
  const params = new URLSearchParams(String(search || '').replace(/^\?/, ''))
  if (params.has('pt_order')) return '#pt-order-list'
  return ''
}

/**
 * 手机访问 PC 站点时，构造 H5 完整 URL（含 origin）
 * @returns {string} 空字符串表示无需跳转
 */
export function buildMobileRedirectToH5Url(locationLike = {}) {
  if (typeof window === 'undefined') return ''
  const hostname = locationLike.hostname ?? window.location.hostname
  if (isLocalDevHost(hostname)) return ''

  const pathname = locationLike.pathname ?? window.location.pathname
  const search = locationLike.search ?? window.location.search
  const hash = locationLike.hash ?? window.location.hash

  if (shouldSkipDeviceRedirect(pathname, search)) return ''
  if (!isPhoneBrowser()) return ''

  const h5Relative = mapPcPathToH5(pathname, search)
  if (!h5Relative) return ''

  const h5Path = h5Relative.startsWith('/') ? h5Relative : `/${h5Relative}`
  const withPrefix = h5Path.startsWith(`${H5_SITE_PREFIX}/`) || h5Path === H5_SITE_PREFIX
    ? h5Path
    : `${H5_SITE_PREFIX}${h5Path}`

  const { pathname: h5Pathname, search: h5Search, hash: h5Hash } = splitPathQueryHash(withPrefix)
  const target = `${window.location.origin}${mergePathQueryHash(h5Pathname, h5Search, h5Hash || hash)}`
  const current = `${window.location.origin}${mergePathQueryHash(pathname, search, hash)}`
  return target === current ? '' : target
}

/**
 * PC / 平板访问 /h5 业务页时，构造 PC 完整 URL
 * @returns {string} 空字符串表示无需跳转
 */
export function buildDesktopRedirectFromH5Url(locationLike = {}) {
  if (typeof window === 'undefined') return ''
  const hostname = locationLike.hostname ?? window.location.hostname
  if (isLocalDevHost(hostname)) return ''

  const pathname = locationLike.pathname ?? window.location.pathname
  const search = locationLike.search ?? window.location.search
  const hash = locationLike.hash ?? window.location.hash

  if (!pathname.startsWith(`${H5_SITE_PREFIX}/`) && pathname !== H5_SITE_PREFIX) return ''
  if (shouldSkipDeviceRedirect(pathname, search)) return ''
  if (!shouldUsePcWebsite()) return ''

  const pcRelative = mapH5PathToPc(pathname, search)
  const { pathname: pcPathname, search: pcSearch } = splitPathQueryHash(pcRelative)
  const pcHash = appendPcVipHash(pcPathname, hash, pcSearch)
  const target = `${window.location.origin}${mergePathQueryHash(pcPathname, pcSearch, pcHash)}`
  const current = `${window.location.origin}${mergePathQueryHash(pathname, search, hash)}`
  return target === current ? '' : target
}

/** @returns {boolean} 是否已触发跳转 */
export function maybeRedirectMobileToH5(locationLike) {
  if (!isDeviceRedirectEnabled({
    search: locationLike?.search ?? (typeof window !== 'undefined' ? window.location.search : ''),
  })) return false
  const url = buildMobileRedirectToH5Url(locationLike)
  if (!url) return false
  window.location.replace(url)
  return true
}

/** @returns {boolean} 是否已触发跳转 */
export function maybeRedirectDesktopFromH5(locationLike) {
  if (!isDeviceRedirectEnabled({
    search: locationLike?.search ?? (typeof window !== 'undefined' ? window.location.search : ''),
  })) return false
  const url = buildDesktopRedirectFromH5Url(locationLike)
  if (!url) return false
  window.location.replace(url)
  return true
}
